import type { GeneralIssue, IssueMap } from '../../../../state/flowIssues';
import type { ClientFlowAction } from '~/graphql/types.client';
import getCardLabel from '../../components/nodeTypes/components/CardTemplate/utils/getCardLabel';

const text = {
  minimumActionError: 'Flow moet minstens twee acties bevatten',
  invalidActionIds:
    'Deze flow bevat ongeldige acties. Corrigeer de acties en probeer opnieuw.',
};

/**
 * Returns the global issues in flow that does not belong in settings or actions
 * @param {Array<ClientFlowAction>} actions - The actions in the flow
 */
const generateGeneralIssues = ({
  actions,
}: {
  actions: Array<ClientFlowAction>;
}): IssueMap => {
  const allIssues = new Map<string, Array<GeneralIssue>>();

  if (actions.length === 1) {
    allIssues.set('flow', [
      {
        type: 'GeneralIssue',
        level: 'warning',
        message: text.minimumActionError,
      },
    ]);
  }

  const invalidActions = actions.reduce(
    (acc, action) => {
      const invalidAction = isInvalidAction(action);
      if (invalidAction) {
        acc.push({ ...invalidAction, label: getCardLabel(action.label) });
      }
      return acc;
    },
    [] as Array<{ message: string; actionId: string; label: string }>,
  );

  if (invalidActions.length > 0) {
    invalidActions.forEach(invalidAction => {
      allIssues.set('flow', [
        {
          type: 'GeneralIssue',
          level: 'error',
          message: `${invalidAction.message}. Ongeldige actie: ${invalidAction.label}`,
        },
      ]);
    });
  }

  return allIssues;
};

const isInvalidAction = (
  action: ClientFlowAction,
): { message: string; actionId: string } | null => {
  if (
    'parentIds' in action &&
    action.parentIds.some((id, index) => action.parentIds.indexOf(id) !== index)
  ) {
    return {
      message: 'Action has duplicate parent IDs',
      actionId: action.id,
    };
  }

  switch (action.__typename) {
    case 'FlowV2_Action_IfElse':
      // IfElse has a child but the child is not in true or false child id
      if (
        action.childrenIds?.length > 0 &&
        !action.trueChildId &&
        !action.falseChildId
      ) {
        return {
          message:
            'IfElse action has a child but the child is not in its true or false child IDs',
          actionId: action.id,
        };
      }

      // IfElse has more than 2 children
      if (action.childrenIds?.length > 2) {
        return {
          message: 'IfElse action has more than two children',
          actionId: action.id,
        };
      }

      return null;

    default:
      // Other actions have more than 1 child
      if (action.childrenIds?.length > 1) {
        return {
          message: 'Non-IfElse action has more than one child',
          actionId: action.id,
        };
      }

      return null;
  }
};

export default generateGeneralIssues;
